import { Fragment, type ReactNode } from "react";
import { ExternalTextLink } from "components/atoms/ExternalTextLink";

type FAQEntry = {
  question: string;
  answer: ReactNode;
  show?: (type: string, servicePackage: string) => boolean;
};

type NewHomesFAQEntry = {
  question: string;
  answer: ReactNode;
};

const isProtectionPlus = (servicePackage: string) =>
  servicePackage.replace(/\s+/g, "") === "protectionplus";

const faqArray: FAQEntry[] = [
  {
    question: "When will I receive my bill?",
    answer:
      "You will receive your first monthly bill approximately 30-40 days after your solar system is activated.",
    show: (type) => type === "monthly" || type === "flexmonthly",
  },
  {
    question: "When will I receive my bill?",
    answer:
      "You will receive a bill for your prepayment amount approximately 30 days your installation is complete. Payment will be due in 30 day.",
    show: (type) => type === "prepaid" || type === "cash",
  },
  {
    question: "When will I receive my bill?",
    answer:
      "You've chosen a Loan financed product; the loan provider will start billing you approximately 60 days after the installation is complete.",
    show: (type) => type === "loan",
  },
  {
    question: "Will I still receive a monthly utility bill?",
    answer:
      "Yes. You'll still receive a monthly bill from your utility which will include any monthly fixed charges, if applicable, and charges for any electricity supplied by them. The System will be connected to the grid in order for it to export excess energy to the grid, as well as allow you to consume energy from your utility as needed.",
  },
  {
    question: "Does my rate ever go up?",
    answer:
      "You may have signed up for an annual escalator which means your monthly payment amount increases one time each year by the escalator amount selected.",
    show: (type) => type === "monthly" || type === "flexmonthly",
  },
  {
    question: "When will I pay a flex charge?",
    answer:
      "In the months that you consume more kWh than your monthly baseline, you will pay the flex charge on every kWh above the baseline up to the maximum flex amount, for was not offset by rollover kWh credits",
    show: (type) => type === "flexmonthly",
  },
  {
    question: "How will my rollover work?",
    answer:
      "In months that you consume less kWh than your monthly baseline, you will receive rollover kWh credits from Sunrun. In the months that you consume more kWh than your monthly baseline, you will apply any available rollover kWh credits applicable to your maximum flex kWh for that given month",
    show: (type) => type === "flexmonthly",
  },
  {
    question: "How long does installation take?",
    answer:
      "The physical install takes 1–2 days, but the full process (permits, utility approval, PTO) usually takes 45–90 days from contract signing.",
  },
  {
    question: "Do I need to be home for the installation?",
    answer:
      "Yes, someone 18+ should be home. We'll coordinate ahead of time to minimize disruption.",
  },
  {
    question: "When will my system be turned on?",
    answer:
      "After the inspection is complete, your utility must grant Permission to Operate (PTO) in order to activate the system. This timeline is vary depending on your utility provider.",
  },
  {
    question: "Is there anything I need to do to maintain the system?",
    answer:
      "Maintaining the system is simple! Just ensure things like trees, foliage or anything on your roof, like soilage on the panels, don't obstruct the system from the sun. Don't tamper with it! If you have a battery, do not update the operating mode. If you have a question, or need to report a problem, contact Sunrun through your Sunrun mobile app or call us 1-855-478-6786.",
    show: (type) =>
      type === "monthly" || type === "flexmonthly" || type === "prepaid",
  },
  {
    question: "Is there anything I need to do to maintain the system?",
    answer:
      "Maintaining the system is simple! Just ensure things like trees, foliage or anything on your roof, like soilage on the panels, don't obstruct the system from the sun. Don't tamper with it! If you have a battery, do not update the operating mode. If you have a question, or need to report a problem, contact Sunrun through your Sunrun mobile app or call us 1-855-478-6786. To help ensure optimal performance of your system, ensure things like trees, foliage or anything on your roof, like soilage on the panels, don't obstruct your system from the sun.",
    show: (type, servicePackage) =>
      (type === "cash" || type === "loan") && isProtectionPlus(servicePackage),
  },
  {
    question: "Is there anything I need to do to maintain the system?",
    answer:
      "To help ensure optimal performance of your system, ensure things like trees, foliage or anything on your roof, like soilage on the panels, don't obstruct your system from the sun.",
    show: (type, servicePackage) =>
      (type === "cash" || type === "loan") && !isProtectionPlus(servicePackage),
  },
  {
    question: "What happens if I sell my home?",
    answer: (
      <>
        If you sell your home, you have options. Most of our customers transfer
        this Agreement to the new homeowner, and we guarantee up front they will
        be eligible to assume the Agreement. You can also purchase the System.
        See&nbsp;
        <ExternalTextLink href="https://servicetransfer.sunrun.com/">
          servicetransfer.sunrun.com
        </ExternalTextLink>
        &nbsp; for more details
      </>
    ),
    show: (type, servicePackage) =>
      type === "monthly" ||
      type === "flexmonthly" ||
      type === "prepaid" ||
      isProtectionPlus(servicePackage),
  },
  {
    question: "What happens at the end of the term of this Agreement?",
    answer:
      "At the end of this Agreement, you can choose to (1) renew this Agreement for a certain period of time; (2) purchase the System; or (3) ask us to remove the System at no additional charge. If you don't let us know which option you prefer, this Agreement will automatically renew for 1 years",
    show: (type) =>
      type === "monthly" || type === "flexmonthly" || type === "prepaid",
  },
  {
    question: "What happens if I need a new roof?",
    answer:
      "If you need to repair your roof, you must have the System removed and replaced by a pre-approved Sunrun contractor.",
    show: (type) =>
      type === "monthly" || type === "flexmonthly" || type === "prepaid",
  },
  {
    question: "How will I know if my system is working?",
    answer:
      "You can monitor System production within the Sunrun mobile app, viewing daily, monthly, yearly production to ensure you are within your Target Zone.",
    show: (type, servicePackage) =>
      type === "monthly" ||
      type === "flexmonthly" ||
      type === "prepaid" ||
      isProtectionPlus(servicePackage),
  },
  {
    question: "Who do I contact if I have questions or need help?",
    answer:
      "You may contact your sales rep, or Customer Experience Specialist if you have any questions or concerns.",
    show: (type, servicePackage) =>
      type === "monthly" ||
      type === "flexmonthly" ||
      type === "prepaid" ||
      isProtectionPlus(servicePackage),
  },
  {
    question: "How do I give a referral and do I get anything for it?",
    answer:
      "Enter your new referral lead on the Sunrun mobile app. When your referral's System is activated, you will receive an email to claim your Referral Reward! Ask your sales rep for complete referral program details.",
    show: (type, servicePackage) =>
      type === "monthly" ||
      type === "flexmonthly" ||
      type === "prepaid" ||
      isProtectionPlus(servicePackage),
  },
];

const newHomesFaqLinkAnswer = (
  intro: string,
  href: string,
  linkLabel = href
) => (
  <>
    {intro}
    <ExternalTextLink href={href}>{linkLabel}</ExternalTextLink>
  </>
);

const newHomesFaqLabeledAnswer = (
  segments: { label: string; text: string }[]
) => (
  <>
    {segments.map((segment, index) => (
      <Fragment key={segment.label}>
        {index > 0 && <br />}
        <strong>{segment.label}</strong>
        {segment.text}
      </Fragment>
    ))}
  </>
);

const newHomesFaqArray: NewHomesFAQEntry[] = [
  {
    question: "Will my system be working when I move in?",
    answer:
      "No, the activation process can take anywhere from 8 to 10 weeks after you close on your home.",
  },
  {
    question: "Can I add more panels or a battery after I move-in?",
    answer: "Contact Sunrun at 1-855-478-6786 to discuss our energy solutions.",
  },
  {
    question: "Will my solar panels work in the event of a power outage?",
    answer: newHomesFaqLabeledAnswer([
      {
        label: "Solar-Only Systems:",
        text: " For safety reasons, standard grid-tied solar systems are designed to shut down automatically during an outage.",
      },
      {
        label: "Solar + Battery Systems:",
        text: " Your home will remain powered. In the event of an outage, your system automatically disconnects from the grid and uses your stored energy to power your designated backup circuits.",
      },
    ]),
  },
  {
    question: "Will I be able to see how much electricity I use?",
    answer:
      "You can view your electricity consumption on your Sunrun App in the monitoring section.",
  },
  {
    question: "Will my solar system cover all of my electricity usage?",
    answer:
      "Most Sunrun New Homes systems will cover a large portion of your electricity usage, but this varies based on system size and how much electricity you consume. We recommend you monitor your Sunrun app for more information.",
  },
  {
    question: "Will I still get a bill from my utility company?",
    answer:
      "Yes, you will receive a bill from your utility company for any excess electricity you use beyond your solar system and any applicable fees charged by your utility company.",
  },
  {
    question: "What do I do if I think there is an issue with my panels?",
    answer: (
      <>
        {newHomesFaqLinkAnswer(
          "You can create a ticket via your Sunrun app. Go to ",
          "https://www.sunrun.com/sunrun-app",
          "www.sunrun.com/sunrun-app"
        )}
        .
      </>
    ),
  },
  {
    question: "Why can't I add more panels before I move in?",
    answer:
      "Your system is permitted prior to the construction of your home, therefore we cannot alter the system size. If you are interested in expanding your system, you can contact us after your system is activated at 1-855-478-6786.",
  },
  {
    question: "Are there any veteran or other discounts available?",
    answer:
      "At this time, we do not have any discounts available in our New Homes program.",
  },
  {
    question: "Are there any extended warranties available?",
    answer:
      "Your system is covered by Sunrun and manufacturer warranties; for specifics regarding your system refer to your customer contract.",
  },
  {
    question: "What happens if I move?",
    answer: newHomesFaqLinkAnswer(
      "We make the process easy and guarantee a successful transfer of your Sunrun Agreement. Start your service transfer in minutes: ",
      "https://www.sunrun.com/moving-made-easy",
      "www.sunrun.com/moving-made-easy"
    ),
  },
  {
    question: "What happens after I turn my system on?",
    answer:
      "When you turn your solar system on, your home will officially be powered with energy from the sun, produced right on your roof. For customers with a monthly bill, you will receive your first bill 30 days after you get the green light to turn on the system.",
  },
  {
    question: "What is a kWh?",
    answer:
      "kWh stands for kilowatt hour. A kilowatt hour is the standard unit of measure for electricity. It represents the amount of energy required to power a 1 kilowatt appliance for one hour. The more power an appliance consumes, the faster it will use up 1 kWh. For example, a power-hungry, 500-watt vacuum cleaner will use up a kWh in two hours, whereas the same amount of energy will power a 100 watt light bulb for 10 hours. Your utility will track how many kWh you buy from them, as well as how many kWh you send into the grid. Sunrun will track how many kWh your solar panels produce.",
  },
  {
    question: "Will my panels work when it's cloudy?",
    answer:
      "Solar panels are capable of producing electricity in all types of weather all year round. You will notice that performance will vary from day to day. On a cloudy day a solar system typically performs at 10% to 25% of its capacity.",
  },
  {
    question: "How do I care for my solar panels?",
    answer:
      "Solar systems generally operate without the need for much care and maintenance but there are a few things you can do as a solar homeowner to make sure you are getting the most out of your solar system. Trim back trees - The more sun on your panels, the better. It's a good idea to keep nearby trees trimmed to allow the sunshine to reach your panels for as much of the day as possible. Spray dusty panels - Just a few rainy days a year is enough to clean your panels. If you live in a particularly dusty area, you may want to hose them off – just do it from the ground to be safe. Watch out for falling snow - It's normal for snow to stick to your panels. There's no need to clean them as the snow will slide off on its own.",
  },
];

export { faqArray, newHomesFaqArray };
export type { FAQEntry, NewHomesFAQEntry };
